import { useCallback, useEffect, useState } from 'preact/hooks';
import { SW_UPDATE_AVAILABLE_EVENT, type ServiceWorkerActivator } from '../main';
import styles from './UpdateAvailableBanner.module.css';

export function UpdateAvailableBanner() {
  const [activate, setActivate] = useState<ServiceWorkerActivator | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const onUpdate = (event: Event) => {
      const ce = event as CustomEvent<{ activate: ServiceWorkerActivator }>;
      if (typeof ce.detail?.activate === 'function') {
        // setState(fn) calls fn — wrap in a thunk so we store the callable itself.
        const next = ce.detail.activate;
        setActivate(() => next);
      }
    };
    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdate);
    return () => window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdate);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!activate || updating) return;
    setUpdating(true);
    try {
      await activate();
      // The page will reload via setupControllerChangeReload when the new
      // worker takes control. If reload doesn't happen (e.g. tests),
      // the banner stays in its updating state — it's about to be gone.
    } catch {
      setUpdating(false);
    }
  }, [activate, updating]);

  const handleDismiss = useCallback(() => {
    setActivate(null);
  }, []);

  if (!activate) return null;

  return (
    <div class={styles.banner} role="status" aria-live="polite">
      <span class={styles.text}>A new version of OurTake is ready.</span>
      <button
        type="button"
        class={styles.action}
        onClick={handleUpdate}
        disabled={updating}
      >
        {updating ? 'Updating…' : 'Update now'}
      </button>
      <button
        type="button"
        class={styles.dismiss}
        onClick={handleDismiss}
        aria-label="Dismiss update notice"
        disabled={updating}
      >
        ×
      </button>
    </div>
  );
}
